import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// oxlint-disable-next-line no-restricted-imports
import { Box } from "@mantine/core";
import { IconGripVertical } from "@tabler/icons-react";
import { FlexRender, type Row, type RowData } from "@tanstack/react-table-v9";
import { type CSSProperties, Fragment, memo, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { getSpacing, spacing } from "#app/theme/v2/spacing";
import { Icon } from "#shared/icons/Icon";

import { Divider } from "../../atoms/Divider/Divider";
import { IconButton } from "../../atoms/IconButton/IconButton";
import { Tooltip } from "../../atoms/Tooltip/Tooltip";
import { DropdownMenu } from "../../molecules/DropdownMenu/DropdownMenu";
import { getColumnCssSizing, getFixedCssSizing } from "./getColumnCssSizing";
import classes from "./Table.module.css";
import { type RowAction, type TableAppFeatures } from "./tableHook";

type TableRowProps<TData extends RowData> = {
  row: Row<TableAppFeatures, TData>;
  isSelected: boolean;
  rowActions?: RowAction<TData>[];
  actionsCellOverlay?: string;
  stickyOffsets?: Record<string, number>;
  lastStickyColumnId?: string;
  enableDnd: boolean;
  isDraggable: boolean;
  dragHandleLabel?: React.ReactNode;
  isLast: boolean;
  onRowClick?: (row: TData) => void;
  height?: string;
  isError?: boolean;
  getCellError?: (row: TData, columnId: string) => boolean;
  showDivider?: boolean;
  renderActionsCell?: boolean;
  actionsRowSpan?: number;
  extraTopPadding?: string;
  extraBottomPadding?: string;
};

export const TABLE_DND_COLUMN_WIDTH = getSpacing(8);
export const TABLE_SELECTION_COLUMN_WIDTH = getSpacing(8);
export const TABLE_ACTIONS_COLUMN_WIDTH = getSpacing(10);

const DEFAULT_ROW_HEIGHT = "72px";

const addPx = (base: string, extra: number) => `${parseInt(base, 10) + extra}px`;

const TableRowImpl = <TData extends RowData>({
  row,
  isSelected,
  rowActions,
  actionsCellOverlay,
  stickyOffsets,
  lastStickyColumnId,
  enableDnd,
  isDraggable,
  dragHandleLabel,
  isLast,
  onRowClick,
  height = DEFAULT_ROW_HEIGHT,
  isError = false,
  getCellError,
  showDivider = true,
  renderActionsCell = true,
  actionsRowSpan = 1,
  extraTopPadding,
  extraBottomPadding,
}: TableRowProps<TData>) => {
  const extraTop = extraTopPadding ? parseInt(extraTopPadding, 10) : 0;
  const extraBottom = extraBottomPadding ? parseInt(extraBottomPadding, 10) : 0;
  const { t } = useTranslation("components");
  const isActionHiddenForRow = (action: NonNullable<typeof rowActions>[number]) =>
    typeof action.hidden === "function" ? action.hidden(row.original) : !!action.hidden;
  const visibleRowActions = rowActions?.filter((action) => !isActionHiddenForRow(action));
  const hasRowActions = !!visibleRowActions?.length;
  const isActionDisabledForRow = (action: NonNullable<typeof rowActions>[number]) =>
    typeof action.disabled === "function" ? action.disabled(row.original) : !!action.disabled;
  const getActionDisabledTooltip = (action: NonNullable<typeof rowActions>[number]) =>
    typeof action.disabledTooltip === "function"
      ? action.disabledTooltip(row.original)
      : action.disabledTooltip;
  const hasVisibleActions = !!visibleRowActions?.some((action) => !isActionDisabledForRow(action));
  const [actionsOpened, setActionsOpened] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !isDraggable,
    attributes: { role: "row" },
  });

  const style: React.CSSProperties = {
    ...(isDraggable
      ? {
          transform: CSS.Transform.toString(transform),
          transition,
        }
      : {}),
    height,
    cursor: onRowClick ? "pointer" : undefined,
  };

  const rowClassName = [
    classes.row,
    isSelected ? classes.rowSelected : "",
    isDragging ? classes.rowDragging : "",
    isError ? classes.rowError : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleRowClick = onRowClick
    ? (event: React.MouseEvent<HTMLTableRowElement>) => {
        const target = event.target as HTMLElement;
        if (target.closest("button, a, input, label, [role='menuitem']")) {
          return;
        }
        onRowClick(row.original);
      }
    : undefined;

  return (
    <>
      <Box
        component="tr"
        ref={isDraggable ? setNodeRef : undefined}
        className={rowClassName}
        style={style}
        onClick={handleRowClick}
        {...(isDraggable ? attributes : {})}
      >
        {enableDnd && (
          <Box
            component="td"
            className={classes.dndCell}
            data-draggable={isDraggable || undefined}
            style={{
              width: TABLE_DND_COLUMN_WIDTH,
              minWidth: TABLE_DND_COLUMN_WIDTH,
              maxWidth: TABLE_DND_COLUMN_WIDTH,
              padding: 0,
              position: "sticky",
              left: 0,
              zIndex: 3,
            }}
          >
            <Tooltip
              label={dragHandleLabel ?? t("table.dragHandle")}
              position="bottom"
              wrap={false}
            >
              <span
                className={classes.dragHandle}
                style={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: TABLE_DND_COLUMN_WIDTH,
                  height: TABLE_DND_COLUMN_WIDTH,
                }}
                {...listeners}
              >
                <IconGripVertical size={16} />
              </span>
            </Tooltip>
          </Box>
        )}
        {row.getVisibleCells().map((cell) => {
          const isSelectCol = cell.column.id === "__select__";
          const sizing = isSelectCol
            ? getFixedCssSizing(TABLE_SELECTION_COLUMN_WIDTH)
            : getColumnCssSizing(cell.column);
          const stickyLeft = stickyOffsets?.[cell.column.id];
          const isSticky = stickyLeft !== undefined;
          const cellOverlay = cell.column.columnDef.meta?.cellOverlay;
          const verticalPadding = cell.column.columnDef.meta?.verticalPadding ?? spacing[3];
          const horizontalPadding = cell.column.columnDef.meta?.horizontalPadding ?? spacing[3];
          const isCellError = !isSelectCol && getCellError?.(row.original, cell.column.id);

          return (
            <Box
              key={cell.id}
              component="td"
              className={isCellError ? classes.cellError : undefined}
              data-has-overlay={cellOverlay ? "" : undefined}
              style={{
                ...(isSelectCol
                  ? {
                      paddingTop: addPx(spacing[3], extraTop),
                      paddingBottom: addPx(spacing[3], extraBottom),
                      paddingLeft: spacing[2],
                      paddingRight: spacing[2],
                    }
                  : {
                      paddingTop: addPx(verticalPadding, extraTop),
                      paddingBottom: addPx(verticalPadding, extraBottom),
                      paddingLeft: horizontalPadding,
                      paddingRight: horizontalPadding,
                    }),
                verticalAlign: "middle",
                textAlign: isSelectCol ? "center" : undefined,
                ...sizing,
                ...(cellOverlay ? ({ "--cell-overlay": cellOverlay } as CSSProperties) : {}),
                ...(isSticky
                  ? {
                      position: "sticky",
                      left: stickyLeft,
                      zIndex: 2,
                      borderRight:
                        cell.column.id === lastStickyColumnId
                          ? `1px solid var(--table-sticky-border-color, transparent)`
                          : undefined,
                    }
                  : {}),
              }}
            >
              <FlexRender cell={cell} />
            </Box>
          );
        })}
        {hasRowActions && renderActionsCell && (
          <Box
            component="td"
            rowSpan={actionsRowSpan}
            data-opened={actionsOpened || undefined}
            data-has-overlay={actionsCellOverlay ? "" : undefined}
            className={classes.actionsCell}
            style={{
              ...(actionsCellOverlay
                ? ({ "--cell-overlay": actionsCellOverlay } as CSSProperties)
                : {}),
              width: TABLE_ACTIONS_COLUMN_WIDTH,
              padding:
                actionsRowSpan > 1
                  ? `${extraTop}px ${spacing[2]} 0 0`
                  : `${spacing[5]} ${spacing[2]} ${spacing[5]} 0`,
              verticalAlign: "top",
              textAlign: "left",
              position: "sticky",
              right: 0,
              zIndex: 2,
            }}
          >
            <Box
              style={
                actionsRowSpan > 1
                  ? {
                      height,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }
                  : undefined
              }
            >
              {hasVisibleActions && (
                <DropdownMenu
                  position="bottom-end"
                  opened={actionsOpened}
                  onOpen={() => setActionsOpened(true)}
                  onClose={() => setActionsOpened(false)}
                >
                  <DropdownMenu.Target>
                    <span className={classes.actionsTrigger}>
                      <IconButton
                        variant="transparent"
                        size="sm"
                        aria-label={t("table.rowActions")}
                      >
                        <Icon name="ellipsis" size={16} color={colors.icon.primaryDefault} />
                      </IconButton>
                    </span>
                  </DropdownMenu.Target>
                  <DropdownMenu.Dropdown>
                    {visibleRowActions.map((action) => {
                      const disabled = isActionDisabledForRow(action);
                      const tooltip = disabled ? getActionDisabledTooltip(action) : undefined;

                      const menuItem = (
                        <DropdownMenu.Item
                          variant={action.variant}
                          leftSection={action.icon}
                          onClick={() => action.onClick(row.original)}
                          disabled={disabled}
                        >
                          {action.label}
                        </DropdownMenu.Item>
                      );

                      if (!tooltip) {
                        return <Fragment key={action.label}>{menuItem}</Fragment>;
                      }

                      return (
                        <Tooltip key={action.label} label={tooltip} position="bottom" wrap={false}>
                          <span style={{ display: "block", width: "100%" }}>{menuItem}</span>
                        </Tooltip>
                      );
                    })}
                  </DropdownMenu.Dropdown>
                </DropdownMenu>
              )}
            </Box>
          </Box>
        )}
      </Box>
      {!isLast && showDivider && (
        <Box component="tr" aria-hidden>
          {enableDnd && (
            <Box
              component="td"
              style={{
                width: TABLE_DND_COLUMN_WIDTH,
                minWidth: TABLE_DND_COLUMN_WIDTH,
                maxWidth: TABLE_DND_COLUMN_WIDTH,
                padding: 0,
                position: "sticky",
                left: 0,
                zIndex: 3,
              }}
            />
          )}
          <Box
            component="td"
            colSpan={row.getVisibleCells().length + (hasRowActions && renderActionsCell ? 1 : 0)}
            style={{ padding: 0 }}
          >
            <Divider type="secondary" />
          </Box>
        </Box>
      )}
    </>
  );
};

// Memoized so a selection/sort/pagination change elsewhere in the table doesn't force every row
// to re-render — only rows whose own props (selection, error state, data, …) actually changed.
const TableRow = memo(TableRowImpl) as <TData extends RowData>(
  props: TableRowProps<TData>,
) => ReturnType<typeof TableRowImpl>;

export { TableRow };
